import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import createBadge from '../utils/createBadge';
import { Typography } from '../typography/Typography';

/**
 * The `Badge` component serves as a visual indicator to highlight a small amount of content.
 * It comes with three variants: outline, regular and strong.
 *
 * Related components: [NotificationBadge](#notificationbadge), [Pill](#pill), [Chip](#chip)
 *
 * Usage:
 *
 * ```jsx
 * import { Badge } from '@one-thd/sui-atomic-components';
 * ```
 */
const Badge = React.forwardRef((props, ref) => {

  const {
    children,
    endIcon: endIconProp,
    startIcon: startIconProp,
    color = 'primary',
    size = 'base',
    variant = 'strong',
    ...other
  } = props;

  const { classes, textColor } = createBadge({
    color,
    variant: variant === 'subtle' ? 'outline' : variant,
    size: size === 'large' ? 'base' : size,
  });

  const isBaseSize = size === 'base' || size === 'large';

  let startIcon = null;
  if (startIconProp && React.isValidElement(startIconProp)) {
    startIcon = React.cloneElement(startIconProp, {
      size: 'inherit'
    });
  }

  let endIcon = null;
  if (endIconProp && React.isValidElement(endIconProp)) {
    endIcon = React.cloneElement(endIconProp, {
      size: 'inherit'
    });
  }

  const rootClasses = classNames(classes, 'sui-inline-flex sui-items-center sui-justify-center sui-gap-1 sui-px-1 sui-whitespace-nowrap', {
    'sui-h-4': size === 'small',
    'sui-h-5': isBaseSize,
  });

  const spanClasses = classNames('sui-overflow-hidden sui-text-ellipsis sui-whitespace-nowrap');

  return (
    <div
      className={rootClasses}
      ref={ref}
      {...other}
    >
      {startIcon}
      <span className={spanClasses}>
        <Typography
          color={textColor}
          height="none"
          variant={size === 'small' ? 'body-xs' : 'body-sm'}
          weight="bold"
        >
          { children }
        </Typography>
      </span>
      {endIcon}
    </div>
  );
});

Badge.displayName = 'Badge';

Badge.propTypes = {
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * The color of the component.
   * @default 'primary'
   */
  color: PropTypes.oneOf(['primary', 'brand', 'success', 'danger', 'medium', 'warning', 'info']),
  /**
   * An icon to display after the content inside the badge.
   * Needs to be a React element type (ie. MyIcon), not an instance (ie. <MyIcon />)
   */
  endIcon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  /**
   * The size of the component. Also affects the font size.
   * @default 'base'
   */
  size: PropTypes.oneOf(['small', 'base']),
  /**
   * An icon to display before the content inside the badge.
   * Needs to be a React element type (ie. MyIcon), not an instance (ie. <MyIcon />)
   */
  startIcon: PropTypes.oneOfType([PropTypes.elementType, PropTypes.node]),
  /**
   * The variant to use.
   * @default 'strong'
   */
  variant: PropTypes.oneOf(['outline', 'regular', 'strong'])
};

export { Badge };
